import type { AuditLogEntry } from '@inou/shared';
import { useEffect, useState } from 'react';

import { AuditLogTable, auditLogFields } from '@/components/audit/AuditLogTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { StateBlock } from '@/components/reports/StateBlock';
import { Button } from '@/components/ui/button';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { useQueryParam } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/**
 * 監査ログ。**管理者だけが開ける**（経路と最低ロールは `lib/nav.ts`）。
 *
 * 記録しているのは書き込みだけ。読み取りで唯一の例外がデータの
 * エクスポートで、全社データの持ち出しはそれ自体が監査対象の事象だから。
 *
 * 行は新しい順に 200 件ずつ読む。月で区切らないのは、記録時刻が
 * Unix 秒で、テナントのタイムゾーンでの月境界を出すには時差の計算が
 * 要るため（作業日を持つ他の一覧とは事情が違う）。
 */
export function AuditLogsPage() {
  const [query, setQuery] = useQueryParam();
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<AuditLogEntry[]>([]);

  const { data, error, loading } = useApi<{
    auditLogs: AuditLogEntry[];
    nextCursor: string | null;
  }>(cursor === null ? '/api/audit-logs' : `/api/audit-logs?before=${cursor}`);

  // 読み込んだページを積む。同じ id は積まない（再取得で二重にしない）。
  useEffect(() => {
    if (!data) return;
    setLoaded((current) => {
      const seen = new Set(current.map((log) => log.id));
      const fresh = data.auditLogs.filter((log) => !seen.has(log.id));
      return fresh.length === 0 ? current : [...current, ...fresh];
    });
  }, [data]);

  const shown = loaded.filter((log) => matchesQuery(query, ...auditLogFields(log)));
  const hasMore = data?.nextCursor != null;

  return (
    <>
      <PageHeader
        title="監査ログ"
        subtitle={
          query.trim() === '' ? `${loaded.length} 件` : `${shown.length} / ${loaded.length} 件`
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        <StateBlock
          // 再取得で画面を白くしない（読み込み済みの行が消える）
          loading={loading && loaded.length === 0}
          error={error}
          empty={loaded.length === 0}
          emptyMessage="記録された操作はまだありません。"
        >
          <div className="border border-line bg-surface-raised">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="操作者・操作・対象で絞り込む"
                className="w-96"
              />
            </div>
            {shown.length === 0 ? (
              <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
                {`「${query}」に一致する記録はありません。`}
              </p>
            ) : (
              <AuditLogTable logs={shown} />
            )}
          </div>

          {hasMore ? (
            <div className="flex justify-center">
              <Button onClick={() => setCursor(data?.nextCursor ?? null)} disabled={loading}>
                {loading ? '読み込み中…' : 'さらに読み込む'}
              </Button>
            </div>
          ) : null}
        </StateBlock>

        <p className="text-xs leading-relaxed text-ink-subtle">
          {
            '記録しているのは書き込みだけです。工数の訂正・削除、単価とロールの変更、招待、MCP トークンの発行と失効、プロジェクトと種別の変更、テナント設定、データのエクスポート、そして運営による操作が残ります。'
          }
          {
            'ログインの履歴と、MCP からの新規の工数記録は含みません（前者はログイン試行の記録が、後者は工数そのものが持っています）。'
          }
          {'絞り込みは読み込み済みの行に対して行います。'}
        </p>
      </div>
    </>
  );
}
